import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'sherlock.sidebar.collapsed';
const TABLET_BP = 900;
const DESKTOP_BP = 1200;

export type SidebarMode = 'sidebar' | 'drawer';

export interface UseSidebarState {
  collapsed: boolean;
  mode: SidebarMode;
  toggle: () => void;
}

function readPersisted(): boolean | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'true') return true;
    if (v === 'false') return false;
    return null;
  } catch {
    return null;
  }
}

function defaultCollapsed(width: number): boolean {
  if (width >= DESKTOP_BP) return false;
  if (width >= TABLET_BP) return true;
  return false; // drawer mode — value is irrelevant; collapsed=false at width<900 means "drawer closed"
}

function modeForWidth(width: number): SidebarMode {
  return width < TABLET_BP ? 'drawer' : 'sidebar';
}

export function useSidebarState(): UseSidebarState {
  const [width, setWidth] = useState<number>(() =>
    typeof window !== 'undefined' ? window.innerWidth : DESKTOP_BP,
  );
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    const persisted = readPersisted();
    if (persisted !== null) return persisted;
    return defaultCollapsed(typeof window !== 'undefined' ? window.innerWidth : DESKTOP_BP);
  });

  useEffect(() => {
    function onResize(): void {
      setWidth(window.innerWidth);
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const toggle = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        /* swallow */
      }
      return next;
    });
  }, []);

  return {
    collapsed,
    mode: modeForWidth(width),
    toggle,
  };
}
